import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import axios from 'axios';
import toast from 'react-hot-toast';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Youtube from '@tiptap/extension-youtube';
import { 
  LuBold, LuItalic, LuList, LuListOrdered, LuQuote, LuHeading1, LuHeading2, 
  LuLink, LuImage, LuUndo, LuRedo, LuType
} from 'react-icons/lu';

const MenuButton = ({ onClick, isActive, disabled, children, title }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    type="button"
    style={{
      padding: '8px',
      borderRadius: '4px',
      border: '1px solid #eee',
      background: isActive ? 'var(--forest)' : '#fff',
      color: isActive ? 'var(--gold-light)' : 'var(--text-body)',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s'
    }}
  >
    {children}
  </button>
);

export const TiptapEditor = ({ content, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({
        allowBase64: true,
      }),
      Link.configure({
        openOnClick: false,
      }),
      Youtube.configure({
        width: 480,
        height: 320,
      }),
    ],
    content: content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  if (!editor) return null;

  const addImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        const toastId = toast.loading('Uploading image...');
        try {
          const formData = new FormData();
          formData.append('file', file);
          const res = await axios.post('/api/blog/upload', formData);
          editor.chain().focus().setImage({ src: res.data.url }).run();
          toast.success('Image ready!', { id: toastId });
        } catch (err) {
          toast.error('Image upload failed', { id: toastId });
        }
      }
    };
    input.click();
  };

  const addLink = () => {
    const url = window.prompt('Enter URL');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <div style={{ border: '1px solid #ddd', borderRadius: '4px', background: '#fff' }}>
      <div style={{ 
        padding: '8px', 
        borderBottom: '1px solid #eee', 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: '4px',
        background: '#fafafa' 
      }}>
        <MenuButton onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive('bold')} title="Bold"><LuBold size={16} /></MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive('italic')} title="Italic"><LuItalic size={16} /></MenuButton>
        <div style={{ width: 1, height: 24, background: '#ddd', margin: '0 4px' }} />
        
        <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} isActive={editor.isActive('heading', { level: 2 })} title="Heading 2"><LuHeading1 size={16} /></MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} isActive={editor.isActive('heading', { level: 3 })} title="Heading 3"><LuHeading2 size={16} /></MenuButton>
        <MenuButton onClick={() => editor.chain().focus().setParagraph().run()} isActive={editor.isActive('paragraph')} title="Paragraph"><LuType size={16} /></MenuButton>
        
        <div style={{ width: 1, height: 24, background: '#ddd', margin: '0 4px' }} />
        <MenuButton onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive('bulletList')} title="Bullet List"><LuList size={16} /></MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive('orderedList')} title="Ordered List"><LuListOrdered size={16} /></MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleBlockquote().run()} isActive={editor.isActive('blockquote')} title="Blockquote"><LuQuote size={16} /></MenuButton>
        
        <div style={{ width: 1, height: 24, background: '#ddd', margin: '0 4px' }} />
        <MenuButton onClick={addLink} isActive={editor.isActive('link')} title="Link"><LuLink size={16} /></MenuButton>
        <MenuButton onClick={addImage} title="Add Image from System"><LuImage size={16} /></MenuButton>
        
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px' }}>
          <MenuButton onClick={() => editor.chain().focus().undo().run()} title="Undo"><LuUndo size={16} /></MenuButton>
          <MenuButton onClick={() => editor.chain().focus().redo().run()} title="Redo"><LuRedo size={16} /></MenuButton>
        </div>
      </div>
      
      <div className="tiptap-wrapper" style={{ padding: '16px', minHeight: '300px' }}>
        <EditorContent editor={editor} />
      </div>

      <style>{`
        .tiptap-wrapper .ProseMirror { outline: none; min-height: 200px; }
        .tiptap-wrapper .ProseMirror p { margin-bottom: 12px; line-height: 1.6; }
        .tiptap-wrapper .ProseMirror h2 { font-family: serif; font-size: 1.5rem; margin: 24px 0 12px; }
        .tiptap-wrapper .ProseMirror h3 { font-family: serif; font-size: 1.2rem; margin: 20px 0 8px; }
        .tiptap-wrapper .ProseMirror ul, .tiptap-wrapper .ProseMirror ol { padding-left: 20px; margin-bottom: 12px; }
        .tiptap-wrapper .ProseMirror blockquote { border-left: 3px solid var(--gold); padding-left: 16px; font-style: italic; color: var(--forest); margin: 20px 0; }
        .tiptap-wrapper .ProseMirror img { max-width: 100%; height: auto; border-radius: 4px; margin: 20px 0; border: 1px solid #eee; }
        .tiptap-wrapper .ProseMirror video { max-width: 100%; border-radius: 8px; margin: 24px 0; display: block; box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
        .tiptap-wrapper .ProseMirror iframe { width: 100%; aspect-ratio: 16/9; border-radius: 4px; margin: 24px 0; }
      `}</style>
    </div>
  );
};

export default TiptapEditor;
